import { useState, useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Select } from '@artosera/ui';
import {
  CHAMPS_SAISIS,
  CHAMPS_PAR_GENRE,
  GENRES_CONTRAT,
  type GenreContrat,
  type Langue,
} from '@artosera/shared';
import {
  useApercuContrat,
  useGenererContrat,
  type CorpsGeneration,
  type ApercuContrat,
} from '../../api/contrats.js';
import { useArtistes } from '../../api/artistes.js';
import { useContact, type ContactPresente } from '../../api/contacts.js';
import { type OeuvrePresentee } from '../../api/oeuvres.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { RechercheOeuvre } from '../ventes/RechercheOeuvre.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { Field } from '../Field.js';
import { erreurContrat } from './contratUi.js';
import styles from './ContratsPage.module.css';
export function NouveauContratDialog({
  galerieSlug,
  ouvert,
  onOuvertChange,
  initial,
  signataireArtiste = false,
}: {
  galerieSlug: string;
  ouvert: boolean;
  onOuvertChange: (v: boolean) => void;
  initial?: { genre?: GenreContrat; artisteId?: string; contactId?: string };
  /** Add-on signature actif : le contrat d'artiste désigne le contact qui signera. */
  signataireArtiste?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const label = (s: string) => t(`regie:contrats.nouveauContrat.${s}`);
  const [genre, setGenre] = useState<GenreContrat>(initial?.genre ?? 'nda');
  const [langue, setLangue] = useState<Langue>('fr');
  const [artisteId, setArtisteId] = useState(initial?.artisteId ?? '');
  const [contactId, setContactId] = useState(initial?.contactId ?? '');
  const [contact, setContact] = useState<ContactPresente | null>(null);
  const fiche = useContact(galerieSlug, contactId || null);
  const [oeuvres, setOeuvres] = useState<OeuvrePresentee[]>([]);
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState('');
  const [touches, setTouches] = useState<ReadonlySet<string>>(new Set());
  const [recherche, setRecherche] = useState('');
  const [rechercheOuverte, setRechercheOuverte] = useState(false);
  const q = useDebounce(recherche);
  const artistes = useArtistes(
    galerieSlug,
    { parPage: 200, ...(q ? { q } : {}) },
    { enabled: genre === 'artiste' && ouvert },
  );
  const champs = CHAMPS_SAISIS.filter((c) =>
    (CHAMPS_PAR_GENRE[genre] as readonly string[]).includes(c),
  );
  const corps: CorpsGeneration = {
    genre,
    langue,
    ...(genre === 'artiste' && artisteId ? { artisteId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(genre === 'depot' ? { oeuvres: oeuvres.map((o) => o.id) } : {}),
    saisies: Object.fromEntries(champs.map((c) => [c, saisies[c] ?? ''])),
  };
  const cle = JSON.stringify(corps);
  const debattue = useDebounce(cle, 400);
  const { mutateAsync: chargerApercu } = useApercuContrat(galerieSlug);
  const generer = useGenererContrat(galerieSlug);
  const [resultat, setResultat] = useState<{ cle: string; apercu: ApercuContrat } | null>(null);
  const [erreurApercu, setErreurApercu] = useState<{ cle: string; texte: string } | null>(null);
  useEffect(() => {
    if (!ouvert) return;
    let annule = false;
    void chargerApercu(JSON.parse(debattue) as CorpsGeneration)
      .then((apercu) => {
        if (!annule) {
          setResultat({ cle: debattue, apercu });
          setErreurApercu(null);
        }
      })
      .catch((e) => {
        if (!annule)
          setErreurApercu({ cle: debattue, texte: erreurContrat(e, t('commun:erreurReseau')) });
      });
    return () => {
      annule = true;
    };
  }, [debattue, chargerApercu, ouvert, t]);
  const apercu = resultat?.cle === cle ? resultat.apercu : null;
  const partieChoisie = genre === 'artiste' ? Boolean(artisteId) : Boolean(contactId);
  const oeuvresChoisies = genre !== 'depot' || oeuvres.length > 0;
  async function soumettre() {
    if (
      !partieChoisie ||
      !oeuvresChoisies ||
      !apercu ||
      apercu.manquants.length ||
      generer.isPending
    )
      return;
    setErreur('');
    try {
      const r = await generer.mutateAsync(corps);
      ouvrirUrlSignee(r.document.url);
      onOuvertChange(false);
    } catch (e) {
      setErreur(erreurContrat(e, t('commun:erreurReseau')));
    }
  }
  return (
    <Dialog
      open={ouvert}
      onOpenChange={onOuvertChange}
      title={label('titre')}
      closeLabel={t('commun:actions.fermer')}
      description={t('regie:contrats.banniereNeutre')}
      size="md"
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void soumettre();
        }}
      >
        <div className={styles.grid}>
          <Field htmlFor="contrat-genre" label={label('genre')}>
            <Select
              id="contrat-genre"
              value={genre}
              onValueChange={(v) => setGenre(v as GenreContrat)}
              options={GENRES_CONTRAT.map((value) => ({
                value,
                label: t(`regie:contrats.genres.${value}`),
              }))}
            />
          </Field>
          <Field htmlFor="contrat-langue" label={label('langue')}>
            <Select
              id="contrat-langue"
              value={langue}
              onValueChange={(v) => setLangue(v as Langue)}
              options={[
                { value: 'fr', label: 'Français' },
                { value: 'en', label: 'English' },
              ]}
            />
          </Field>
        </div>
        {genre === 'artiste' ? (
          <fieldset>
            <legend>{label('artiste')}</legend>
            <Button type="button" onClick={() => setRechercheOuverte((v) => !v)}>
              {label('rechercherArtiste')}
            </Button>
            {rechercheOuverte && (
              <Input
                type="search"
                aria-label={label('rechercherArtiste')}
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
              />
            )}
            <Select
              aria-label={label('artiste')}
              value={artisteId || '__choisir'}
              onValueChange={(v) => setArtisteId(v === '__choisir' ? '' : v)}
              options={[
                { value: '__choisir', label: '—' },
                ...(artistes.data?.artistes ?? []).map((a) => ({ value: a.id, label: a.nom })),
              ]}
            />
          </fieldset>
        ) : (
          <fieldset>
            <legend>{label('contact')}</legend>
            <RechercheContact
              galerieSlug={galerieSlug}
              contact={contact ?? fiche.data?.contact ?? null}
              onChoisir={(c) => {
                setContact(c);
                setContactId(c.id);
              }}
              onEffacer={() => {
                setContact(null);
                setContactId('');
              }}
            />
          </fieldset>
        )}
        {genre === 'artiste' && signataireArtiste && (
          <Field htmlFor="contact-signataire-artiste" label={t('regie:signatures.contactArtiste')}>
            <RechercheContact
              galerieSlug={galerieSlug}
              contact={contact ?? fiche.data?.contact ?? null}
              onChoisir={(c) => {
                setContact(c);
                setContactId(c.id);
              }}
              onEffacer={() => {
                setContact(null);
                setContactId('');
              }}
            />
          </Field>
        )}
        {genre === 'depot' && (
          <fieldset>
            <legend>{label('oeuvres')}</legend>
            {oeuvres.map((o) => (
              <div key={o.id} className={styles.actions}>
                <span>
                  {o.artisteNom} — {o.titre}
                </span>
                <Button
                  type="button"
                  onClick={() => setOeuvres((l) => l.filter((a) => a.id !== o.id))}
                >
                  {t('regie:contrats.modele.retirer')}
                </Button>
              </div>
            ))}
            <RechercheOeuvre
              galerieSlug={galerieSlug}
              onChoisir={(o) => setOeuvres((l) => (l.some((a) => a.id === o.id) ? l : [...l, o]))}
            />
          </fieldset>
        )}
        <div className={styles.grid}>
          {champs.map((c) => (
            <Field
              key={c}
              htmlFor={`contrat-${c}`}
              label={t(`regie:contrats.saisies.${c.slice(7)}`)}
            >
              <Input
                id={`contrat-${c}`}
                value={saisies[c] ?? ''}
                aria-invalid={(touches.has(c) && apercu?.manquants.includes(c)) || undefined}
                onBlur={() => setTouches((precedents) => new Set([...precedents, c]))}
                className={
                  touches.has(c) && apercu?.manquants.includes(c) ? styles.manquant : undefined
                }
                onChange={(e) => setSaisies((s) => ({ ...s, [c]: e.target.value }))}
              />
            </Field>
          ))}
        </div>
        {erreurApercu?.cle === cle && <p role="alert">{erreurApercu.texte}</p>}
        {erreur && <p role="alert">{erreur}</p>}
        {apercu ? (
          <>
            <h2>{label('apercu')}</h2>
            {apercu.manquants.length > 0 && (
              <div role="status">
                <p>{label('manquants')}</p>
                <ul>
                  {apercu.manquants.map((c) => (
                    <li key={c}>
                      {c.startsWith('saisie.') ? t(`regie:contrats.saisies.${c.slice(7)}`) : c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <FeuillePiece
              emetteur={apercu.valeurs['galerie.raisonSociale'] ?? galerieSlug}
              nature={t(`regie:contrats.genres.${genre}`)}
              destinataire={{
                legende: genre === 'artiste' ? label('artiste') : label('contact'),
                nom: apercu.valeurs[genre === 'artiste' ? 'artiste.nom' : 'contact.nom'] ?? '—',
              }}
              numero={apercu.titre}
              brouillon
              pied={t('regie:contrats.aValider')}
            >
              <div className={styles.apercu}>
                <p>{apercu.preambule}</p>
                {apercu.articles.map((a, i) => (
                  <section key={i}>
                    <h3>{a.titre}</h3>
                    <p>{a.texte}</p>
                  </section>
                ))}
                <p>{apercu.fait}</p>
              </div>
            </FeuillePiece>
          </>
        ) : (
          <p role="status">{t('commun:chargement')}</p>
        )}
        <Button
          type="submit"
          loading={generer.isPending}
          disabled={!partieChoisie || !oeuvresChoisies || !apercu || apercu.manquants.length > 0}
        >
          {label('generer')}
        </Button>
      </form>
    </Dialog>
  );
}
